import React, { memo, useEffect, useState } from 'react';

import { faSpinner } from '@fortawesome/fontawesome-free-solid';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import {
  ReactComponent as ChevronDown,
} from '../../assets/svg/chevron_down.svg';
import {
  ReactComponent as ChevronRight,
} from '../../assets/svg/chevron_right.svg';
import { MenuPinType } from "../../shared/interfaces";
import { Fa } from '../elements/fa';
import { MenuPin } from '../elements/MenuPin';
import styles from './Tile.module.scss';

interface iTileProps {
  children: any,
  title?: React.ReactNode,
  style?: React.CSSProperties,
  loading?: boolean,
  action?: JSX.Element,
  collapsible?: boolean,
  initialCollapse?: boolean,
  deviceId?: string,
  property?: string,
  isPinned?: boolean,
  withPin?: boolean,
  order?: number,
  leftIcon?: React.FC<React.SVGProps<SVGSVGElement> & { title?: string }>,
  tileBodyClassName?: string,
  titleClassname?: string,
  leftSide?: {
    link: string,
    onClick?: () => void,
  },
  extraInfoValues?: any;
  renderValue?: any;
}

const tileStyle: React.CSSProperties = {
  width: '100%',
  userSelect: 'none',
};

const iconStyle: React.CSSProperties = {
  width: '10px',
  height: '10px',
  color: '#888888'
}

const Title = ({
  text,
  action = false,
  collapsible,
  collapsed,
  toggle,
  loading = false,
  deviceId,
  property,
  isPinned,
  withPin,
  leftIcon,
  className,
  leftSide,
  extraInfoValues,
  renderValue,
}: any) => {
  const ChevronIcon = collapsed ? <ChevronRight style={iconStyle} /> : <ChevronDown style={iconStyle} />;
  const shouldBeCollapsible = !(property === MenuPinType.EXTRA_INFO);
  const LeftSide = memo(({ children }) => {
    if (leftSide) {
      return <Link
        to={leftSide.link}
        className={styles.leftSide}
        onClick={leftSide?.onClick}
      >
        {children}
      </Link>
    }
    else {
      return <div className={styles.leftSide}>
        {children}
      </div>
    }
  })

  return (
    <section className={classNames(className, styles.StandardTileTitle)} onClick={toggle} style={!shouldBeCollapsible ? { cursor: 'auto' } : {}}>
      <LeftSide>
        {typeof leftIcon === 'string'
          ? <img src={leftIcon} style={{flexShrink: 0}} alt='tile icon' /> :
          typeof leftIcon === 'function' ? <span style={{flexShrink: 0}}>{leftIcon()}</span> : null
        }
        <p className={styles.titleText}>
          {text}
          {extraInfoValues && extraInfoValues?.length > 0 &&
            extraInfoValues.map(([labelName, value], index) => (
              <>
                {' | '}
                <span className={styles.infoItem__label}>{labelName}: </span>
                <span className={styles.infoItem__value}>{renderValue(value)}</span>
              </>
            ))}
        </p>
      </LeftSide>

      <div className={styles.rightSide}>
        {withPin && !collapsed && shouldBeCollapsible && <MenuPin property={property} isPinnedProp={isPinned} />}
        {!action ? null : action}
        {loading
          ? <Fa spin icon={faSpinner} />
          : !collapsible || !shouldBeCollapsible
            ? null
            : ChevronIcon
        }
      </div>
    </section>
  )
}

const StandardTile = (props: iTileProps) => {
  const { style = {},
    title,
    action,
    collapsible,
    children,
    loading = false,
    deviceId,
    property,
    isPinned,
    initialCollapse,
    withPin,
    order,
    leftIcon,
    tileBodyClassName,
    titleClassname,
    leftSide,
    extraInfoValues,
    renderValue,
  } = props;
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    return property ? !isPinned : !!props.initialCollapse;
  });
  const shouldBeCollapsible = !(property === MenuPinType.EXTRA_INFO);

  useEffect(() => {
    const value = property ? !isPinned : !!props.initialCollapse;
    setCollapsed(value);
  }, [isPinned, initialCollapse]);


  return (
    <div style={{ ...tileStyle, ...style, order: order }}>
      <Title
        loading={loading}
        text={title}
        action={action}
        collapsible={collapsible}
        collapsed={collapsed}
        toggle={() => {
          if (shouldBeCollapsible && collapsible) {
            setCollapsed(!collapsed)
          }
        }}
        deviceId={deviceId}
        property={property}
        isPinned={isPinned}
        withPin={withPin}
        order={order}
        className={titleClassname}
        leftIcon={leftIcon}
        leftSide={leftSide}
        extraInfoValues={extraInfoValues}
        renderValue={renderValue}
      />
      <div className={classNames(!collapsed && tileBodyClassName, styles.StandardTileBody, { [styles.expanded]: !collapsed },)}>
        {collapsed ? null : children}
      </div>
    </div>
  )
}

export default StandardTile;